import { z } from "zod";

/**
 * DAVET / ŞİFRE SIFIRLAMA BAĞLANTISI — saf çözümleme
 *
 * ================================================================
 * NEDEN AYRI VE SAF
 * ================================================================
 * Sunucu iki bağlantı üretiyor (`jeton.service.ts`):
 *
 *   ${APP_URL}/davet?jeton=…
 *   ${APP_URL}/sifre-sifirla?jeton=…
 *
 * `APP_URL` panele bakıyor. Panelin yönlendiricisi yok; hangi sayfanın
 * açılacağına adres çubuğuna bakarak karar veriliyor. Bu karar
 * `window` olmadan test edilebilsin diye buraya alındı — yanlış
 * çözümlenen bir bağlantı, kullanıcıyı davet ekranı yerine giriş
 * ekranında bırakır ve kimse sebebini anlamaz.
 */

export type JetonAkisi = "davet" | "sifirlama" | "anket";

export interface JetonSayfasi {
  akis: JetonAkisi;
  /** Adresteki ham jeton. Boşsa sayfa "bağlantı geçersiz" der. */
  jeton: string;
}

/** Sunucunun ürettiği yollar — `jeton.service.ts` ile aynı olmalı. */
const YOLLAR: Record<string, JetonAkisi> = {
  "/davet": "davet",
  "/sifre-sifirla": "sifirlama",
};

/**
 * ANKET BAĞLANTISI SORGU DEĞİL YOL PARÇASI.
 *
 * `anket.service.ts` bildirimde `/anket/{jeton}` üretiyor — ötekiler
 * gibi `?jeton=` değil. Aynı kalıba zorlamak sunucuyu değiştirmek
 * olurdu; çözümleyici iki biçimi de tanıyor.
 */
const ANKET_ONEKI = "/anket/";

/**
 * Adresten jeton sayfasını çıkarır; jeton sayfası değilse `null`.
 *
 * @param yol   `location.pathname`
 * @param sorgu `location.search`
 */
export function jetonSayfasiCozumle(yol: string, sorgu: string): JetonSayfasi | null {
  // Sondaki `/` bilerek yok sayılıyor: e-posta istemcileri bağlantıyı
  // bazen `/davet/` biçiminde sarmalıyor ve o tek karakter yüzünden
  // sayfanın açılmaması, teşhis edilmesi en zor arızalardan olurdu.
  const temiz = yol.replace(/\/+$/, "") || "/";

  if (temiz.startsWith(ANKET_ONEKI)) {
    return { akis: "anket", jeton: decodeURIComponent(temiz.slice(ANKET_ONEKI.length)).trim() };
  }

  const akis = YOLLAR[temiz];
  if (!akis) return null;

  const jeton = new URLSearchParams(sorgu).get("jeton") ?? "";
  return { akis, jeton: jeton.trim() };
}

/**
 * Bağlantı jetonu makul uzunlukta mı.
 *
 * Sunucu `min(20)` istiyor. Aynı eşiği burada da uygulamak, boş ya da
 * kırpılmış bir bağlantıda kullanıcıya ŞİFRE YAZDIRMADAN "bağlantı
 * geçersiz" diyebilmek için: yazdırıp sonra reddetmek, kullanıcıya
 * şifresinin sorunlu olduğunu düşündürürdü.
 */
export function jetonGecerliGorunuyorMu(jeton: string): boolean {
  return jeton.length >= 20;
}

/**
 * AKTIVASYONDA ZORUNLU ONAY — F-LGL-03a (Emre, 24.10.2026)
 *
 * "Hesap açılırken zorunlu metinlerin onay kutusu gelir, onaylamadan aktivasyon tamamlanmaz." Hangi metinlerin
 * istendiğine SUNUCU karar verir (`POST /auth/davet/onay-metinleri`): ekran ne yaş hesaplar ne tür listesi tutar.
 * 18 yaş altı öğrencide liste boş döner (zorunlu rızayı veli verir) ve ekran hiç kutu çizmez.
 */
export interface DavetOnayMetni {
  tur: string;
  /** Kullanıcıya gösterilen metnin sürümü — onayla birlikte geri gönderilir. */
  surum: string;
  baslik: string;
  govde: string;
}

const jetonAlani = z.string().min(20, "Bağlantı geçersiz.");

export const aktivasyonOnayiSemasi = z.object({
  tur: z.string().min(1).max(64),
  surum: z.string().min(1).max(64),
});

/** `POST /auth/daveti-tamamla` gövdesi. `onaylar` verilmezse boş sayılır: zorunlu metin varken sunucu reddeder. */
export const davetiTamamlaSemasi = z.object({
  jeton: jetonAlani,
  sifre: z.string().min(1, "Şifre boş olamaz."),
  onaylar: z.array(aktivasyonOnayiSemasi).max(20).default([]),
});

export const davetOnayMetinleriIstegiSemasi = z.object({ jeton: jetonAlani });

/** Sunucunun aktivasyon reddinde gönderdiği iki kod — jeton reddinden (400 + BULUNAMADI…) ayrı. */
export const AKTIVASYON_RIZA_KODLARI: readonly string[] = ["RIZA_EKSIK", "RIZA_SURUMU_ESKI"];

/**
 * Onay kutusunun anahtarı TÜR + SÜRÜM (sürüm 13 okuma turu, Codex 45 #1). Yalnız türe bağlıyken metin sorgusu
 * yenilenip yeni sürüm gelince eski işaret yerinde kalıyor ve kullanıcı yeni metni hiç onaylamadan yeni sürüm
 * gönderiliyordu; sunucu güncel sürümü gördüğü için reddetmiyordu. Sürüm değişince anahtar değişir, kutu boşalır.
 */
export function onayAnahtari(m: { tur: string; surum: string }): string {
  return `${m.tur}@${m.surum}`;
}

/** Her metin için bir kutu işaretli mi — işaretlenmeden gönder düğmesi pasif. */
export function aktivasyonOnaylariTamMi(
  metinler: readonly DavetOnayMetni[],
  isaretli: Readonly<Record<string, boolean>>,
): boolean {
  return metinler.every((m) => isaretli[onayAnahtari(m)] === true);
}

/** İşaretli kutuları sunucuya gidecek onay listesine çevirir: kullanıcının GÖRDÜĞÜ sürümle. */
export function aktivasyonOnaylari(
  metinler: readonly DavetOnayMetni[],
  isaretli: Readonly<Record<string, boolean>>,
): Array<{ tur: string; surum: string }> {
  return metinler
    .filter((m) => isaretli[onayAnahtari(m)] === true)
    .map((m) => ({ tur: m.tur, surum: m.surum }));
}

/** Ekranda kullanılan metinler — iki akış aynı bileşeni paylaşıyor. */
/**
 * MUVAFAKAT BEKLEYEN HESAP — F-PPL-05b
 *
 * ================================================================
 * "HESABINIZ ACILDI" HER ZAMAN DOGRU DEGIL
 * ================================================================
 * 18 yas alti ogrencide veli muvafakati yoksa sifre YAZILIYOR ama
 * hesap `INVITED` kaliyor. Giris tarafi `status !== "ACTIVE"`
 * oldugunda — sifre DOGRU olsa bile — reddediyor ve G-AUT-06 geregi
 * "E-posta veya sifre hatali." diyor.
 *
 * Yani bu ayrimi yapmasaydik kullaniciyi kendi dogru sifresiyle
 * "sifren yanlis" duvarina gonderirdik ve sebebini hicbir ekranda
 * goremezdi. Cumle NE OLDUGUNU ve SIRADA NE OLDUGUNU soyluyor.
 */
export const MUVAFAKAT_BEKLIYOR_METNI = {
  bitti:
    "Şifreniz kaydedildi. Hesabınız, veliniz muvafakat formunu " +
    "onayladığında açılacak — o ana kadar giriş yapamazsınız.",
  altNot:
    "Bu bir hata değil: 18 yaşından küçük öğrencilerde veli onayı " +
    "olmadan hesap açılmıyor. Onay geldiğinde ayrıca haber verilecek; " +
    "gecikirse kurumunuza sorun.",
} as const;

export const AKIS_METNI: Record<
  JetonAkisi,
  { baslik: string; aciklama: string; dugme: string; bitti: string; altNot: string }
> = {
  davet: {
    baslik: "Hesabınızı açın",
    aciklama:
      "Şifrenizi siz belirlersiniz — sizi davet eden kişi de dahil kimse şifrenizi göremez (F-PPL-01b).",
    dugme: "Şifreyi belirle ve hesabı aç",
    bitti: "Hesabınız açıldı. Artık giriş yapabilirsiniz.",
    /*
      TEK KULLANIMLIK OLDUGU OLCULDU: `jeton.service.ts` davet
      tamamlanirken `usedAt` damgasini SIFRE YAZIMIYLA AYNI islemde
      basiyor. Cizim ("DavetKabul.dc.html") bu satirda "sonraki adimda
      iki asamali dogrulama kurulumu var; atlanamaz" yaziyordu — panel
      kodunda o adim ATLANABILIR bir hatirlatma bandi (`Uygulama.tsx`
      `ikiAsamaHatirlat`), zorunlu bir adim degil. Cizimdeki cumleyi
      kopyalamak, olmayan bir davranisi vaat etmek olurdu.
    */
    altNot: "Bu bağlantı tek kullanımlıktır; şifrenizi belirledikten sonra bir daha açılmaz.",
  },
  sifirlama: {
    baslik: "Yeni şifre belirleyin",
    aciklama:
      "Şifre değişince açık olan bütün oturumlar kapanır — hesabınıza başkası girdiyse o da düşer.",
    dugme: "Şifreyi değiştir",
    bitti: "Şifreniz değişti. Yeni şifrenizle giriş yapabilirsiniz.",
    /*
      IKI IDDIA DA IZLENDI: `sifreyiSifirla` once
      `cihazlar.hepsiniIptalEt`, sonra `sessions.hepsiniSonlandir`
      cagiriyor. Guvenilir cihaz isareti dustugu icin bir sonraki
      giriste dogrulama kodu yeniden soruluyor — cumlenin ikinci
      yarisi da olculdu, tahmin degil.
    */
    altNot:
      "Güvenilir cihaz işaretleri de silinir — bir sonraki girişinizde doğrulama kodu yeniden sorulur.",
  },
  /*
    Anket şifre formu KULLANMIYOR; ayrı bir bileşen çiziliyor. Metin
    yine burada duruyor ki `JetonAkisi` üzerindeki `Record` eksiksiz
    kalsın — eksik bırakmak, yeni bir akış eklendiğinde derleyicinin
    uyarmasını engellerdi.
  */
  anket: {
    baslik: "Memnuniyet anketi",
    aciklama: "Birkaç soru; giriş yapmanız gerekmiyor.",
    dugme: "Gönder",
    bitti: "Yanıtınız kaydedildi. Teşekkür ederiz.",
    /*
      ANKET BU BILESENI KULLANMIYOR; alan yalnizca `Record` eksiksiz
      kalsin diye var. Dogrulanmamis bir vaat yazmamak icin metin
      akisin ZATEN dogrulanmis ozelligini tekrarliyor: uc
      `@HerkeseAcik()`.
    */
    altNot: "Bu anket için giriş yapmanız gerekmiyor.",
  },
};

/**
 * ŞİFRE ASGARİ UZUNLUKLARI — sunucudaki `minLengthFor` ile AYNI sayılar.
 *
 * Sunucu `isStaff ? 10 : 8` diyor; buradaki iki sabit onun karşılığı.
 * İkisi ayrışırsa kabul edilen bir şifre ekranda reddedilmiş, ya da
 * reddedilecek bir şifre kabul edilecekmiş gibi görünür.
 */
export const PERSONEL_EN_AZ = 10;

/**
 * Öğrenci ve veli hesaplarının asgarisi.
 *
 * ================================================================
 * SABİT YOKTU VE GÜÇ ÇUBUĞU YANLIŞ TARAFA YANILIYORDU
 * ================================================================
 * `sifreGucu` eşiği sabit olarak `PERSONEL_EN_AZ` (10) alıyordu.
 * Sonucu: öğrencinin **kabul edilecek** 8 karakterlik şifresi ekranda
 * bir puan eksik, yani daha ZAYIF görünüyordu.
 *
 * Yanlış tarafa yanılma bu: kullanıcı sisteminin kabul edeceği bir
 * şifreyi "yetersiz" sanıp değiştirmeye çalışır ve çubuğa güveni
 * biter. Ters yön (zayıf şifreyi güçlü göstermek) daha tehlikeli
 * olurdu ama bu da bir bozukluk.
 *
 * Jeton ekranları öğrenci uygulamasına taşındığında görünür olacaktı;
 * sabit şimdi tanımlandı ki taşıyan kişi aramak zorunda kalmasın.
 */
export const OGRENCI_EN_AZ = 8;

export interface SifreFormu {
  sifre: string;
  tekrar: string;
}

export type SifreHatalari = Partial<Record<keyof SifreFormu, string>>;

/**
 * Formun KENDİ kontrolü. Politikanın tamamı sunucuda.
 *
 * Burada yalnızca sunucuya gitmeden anlaşılabilecek iki şey var: alan
 * boş mu ve iki alan tutuyor mu. Uzunluk/karmaşıklık kurallarını
 * kopyalamak, iki tarafın ayrışacağı bir yer daha açardı — sunucu
 * `ayrinti.sifre` ile zaten konuşuyor.
 */
export function sifreDogrula(f: SifreFormu): SifreHatalari {
  const h: SifreHatalari = {};
  if (f.sifre.length === 0) h.sifre = "Şifre boş olamaz.";
  if (f.tekrar.length === 0) h.tekrar = "Şifreyi tekrar yazın.";
  else if (f.sifre !== f.tekrar) h.tekrar = "İki şifre aynı değil.";
  return h;
}

/**
 * ŞİFRE GÜCÜ — 0..4
 *
 * ================================================================
 * BU BİR POLİTİKA DEĞİL, GERİ BİLDİRİM
 * ================================================================
 * Kabul kararını SUNUCU veriyor (`password.ts`); bu fonksiyon yalnızca
 * çizimdeki dört çubuğu dolduruyor. İkisini karıştırmak tehlikeli
 * olurdu: burada "güçlü" görünen bir şifre sunucuda reddedilirse
 * kullanıcı ekranın yalan söylediğini düşünür.
 *
 * O yüzden ölçüt DAR ve gözlemlenebilir: uzunluk ve karakter çeşidi.
 * Sözlük kontrolü, sızmış şifre sorgusu gibi şeyler burada YOK —
 * onlar sunucunun işi ve istemcide yapılamaz.
 *
 * Sıfır dönmesi "zayıf" değil ÖLÇÜLMEDİ demek: boş bir alanda çubuk
 * hiç çizilmiyor (bkz. çağıran taraf), çünkü boş bir şifre hakkında
 * "zayıf" demek de bir iddia olurdu.
 */
export function sifreGucu(
  sifre: string,
  /**
   * O HESAP TÜRÜNÜN asgarisi — `PERSONEL_EN_AZ` ya da `OGRENCI_EN_AZ`.
   *
   * ZORUNLU ve varsayılanı YOK. Varsayılan koysaydık (eskiden fiilen
   * `PERSONEL_EN_AZ` sabitti) öğrenci uygulamasına taşıyan kişi
   * parametreyi geçmeyi unutur ve hata SESSİZ olurdu: çubuk çizilir,
   * yalnızca yanlış çizilir. Zorunlu parametre bunu derleme hatasına
   * çeviriyor.
   */
  enAz: number,
): 0 | 1 | 2 | 3 | 4 {
  if (sifre.length === 0) return 0;

  let puan = 0;
  if (sifre.length >= enAz) puan++;
  if (sifre.length >= enAz + 4) puan++;
  // Cesit: kucuk+buyuk, rakam, sembol — her biri bir puan.
  if (/[a-zçğıöşü]/.test(sifre) && /[A-ZÇĞİÖŞÜ]/.test(sifre)) puan++;
  if (/\d/.test(sifre) && /[^\w\sçğıöşüÇĞİÖŞÜ]/.test(sifre)) puan++;

  return Math.min(puan, 4) as 0 | 1 | 2 | 3 | 4;
}

/** Güç seviyesinin adı ve rengi — renk TEK BAŞINA anlam taşımıyor. */
export const GUC_ADI: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "",
  1: "Zayıf",
  2: "Orta",
  3: "İyi",
  4: "Güçlü",
};

/**
 * SUNUCU HATASI HANGİSİNİ SUÇLUYOR — bağlantıyı mı, şifreyi mi?
 *
 * ================================================================
 * NEDEN AYIRT ETMEK ZORUNDAYIZ
 * ================================================================
 * İkisi kullanıcıya BAMBAŞKA iki iş emri veriyor:
 *
 *   şifre reddedildi   → başka bir şifre yaz, bağlantın hâlâ geçerli
 *   bağlantı reddedildi → yazacağın hiçbir şifre işe yaramaz,
 *                         yeni bağlantı iste
 *
 * Ayırmasaydık ikinci durumdaki kullanıcı, ölü bir bağlantının
 * altında şifre denemeye devam ederdi.
 *
 * ================================================================
 * AYIRT EDİCİ ARTIK `kod` — VE BU BİR DÜZELTMEDİR
 * ================================================================
 * İlk yazımda ölçüt `ayrinti.sifre`nin VARLIĞIYDI, çünkü o gün
 * `auth.controller.ts` her iki hatayı da tek `kod: "DOGRULAMA"` ile
 * döndürüyordu ve `JetonRedError.neden` yolda kayboluyordu.
 *
 * Aynı gün (04.09.2026) sunucu düzeltildi: `kod` artık nedenin
 * kendisini taşıyor. Düzeltmeyle birlikte eski ölçüt SESSİZCE BOZULDU
 * — sunucu `SURESI_GECMIS` gönderiyor, istemci yalnızca `DOGRULAMA`
 * tanıyordu, yani süresi dolmuş bağlantı ölü-bağlantı ekranını hiç
 * açmıyor, formun içinde genel bir hata kutusu çıkıyordu.
 *
 * Buradaki asıl ders sözleşmede: iki tarafın anlaştığı alan
 * DEĞİŞTİĞİNDE derleyici hiçbir şey söylemiyor, çünkü `kod` bir
 * `string`. Aşağıdaki liste bu yüzden ADLANDIRILDI ve testi var.
 *
 * ================================================================
 * "ŞİFRE DEĞİLSE JETONDUR" DEMİYOR
 * ================================================================
 * Yalnızca 400 + BİLİNEN ÜÇ NEDEN jeton reddi sayılıyor. Ağ kopması,
 * 429, 500, tanımadığımız bir `kod` — hepsi `genel`. Aksi hâlde arka
 * uç kapalıyken kullanıcıya "bağlantınızın süresi dolmuş" derdik ve
 * bu, sistemin söyleyebileceği en pahalı yalanlardan biri olurdu:
 * kullanıcı çalışan bağlantısını çöpe atıp yenisini isterdi.
 */
export type JetonHataSinifi = "jeton" | "sifre" | "genel";

/**
 * Sunucunun jeton reddinde gönderdiği üç kod — `JetonRedNedeni`.
 *
 * `BULUNAMADI` paylaşılan hata kataloğunda "aradığınız kayıt yok"
 * anlamına da geliyor; karışmıyor çünkü o 404 ile gelir, jeton reddi
 * ise 400. Durum kodu kontrolü bu yüzden kaldırılamaz.
 */
const JETON_RED_KODLARI: readonly string[] = ["BULUNAMADI", "SURESI_GECMIS", "KULLANILMIS"];

export function jetonHatasiniSinifla(hata: {
  durum: number;
  kod: string;
  ayrintiSifre?: string;
}): JetonHataSinifi {
  if (hata.ayrintiSifre) return "sifre";
  if (hata.durum === 400 && JETON_RED_KODLARI.includes(hata.kod)) return "jeton";
  return "genel";
}
